// src/ts/components/components.ts
// This file provides granular initializers for UI components.
// It separates base components (for everyone) from auth-dependent ones.

import { initializeSlider } from "./slider";
import { initThemeToggler } from "./theme-toggle";
import { initializeDropdown, type DropdownInstance } from "./dropdown";

// Keep track of all active dropdowns on the page to handle closing them.
const activeDropdowns: DropdownInstance[] = [];

/**
 * Finds all dropdowns on the page and initializes them.
 */
function initializeAllDropdowns(): void {
  // ... (no changes in the function body, it's correct)
  document.querySelectorAll<HTMLElement>('[data-component="dropdown"]').forEach((container) => {
    const toggle = container.querySelector<HTMLElement>("[data-dropdown-toggle]");
    const content = container.querySelector<HTMLElement>("[data-dropdown-content]");

    if (!toggle || !content) {
      console.warn("Dropdown container is missing toggle or content child", container);
      return;
    }
    const toggleId = toggle.id || `dd-toggle-${Math.random().toString(36).substr(2, 9)}`;
    const contentId = content.id || `dd-content-${Math.random().toString(36).substr(2, 9)}`;
    toggle.id = toggleId;
    content.id = contentId;

    const dropdownInstance = initializeDropdown({
      toggleSelector: `#${toggleId}`,
      contentSelector: `#${contentId}`,
    });

    if (dropdownInstance) {
      activeDropdowns.push(dropdownInstance);
      dropdownInstance.toggle.addEventListener("click", () => {
        if (dropdownInstance.content.classList.contains(dropdownInstance.openClass)) {
          activeDropdowns.forEach((otherDropdown) => {
            if (
              otherDropdown !== dropdownInstance &&
              otherDropdown.content.classList.contains(otherDropdown.openClass)
            ) {
              otherDropdown.toggle.setAttribute("aria-expanded", "false");
              otherDropdown.content.classList.remove(otherDropdown.openClass);
            }
          });
        }
      });
    }
  });

  if (activeDropdowns.length > 0) {
    document.addEventListener("click", (e: MouseEvent) => {
      const target = e.target as Node;
      activeDropdowns.forEach((dropdown) => {
        if (
          dropdown.content.classList.contains(dropdown.openClass) &&
          !dropdown.toggle.contains(target) &&
          !dropdown.content.contains(target)
        ) {
          dropdown.toggle.setAttribute("aria-expanded", "false");
          dropdown.content.classList.remove(dropdown.openClass);
        }
      });
    });
  }
}

/**
 * Initializes all sliders on the page that have not yet been initialized.
 * It uses a `data-initialized` flag to prevent re-initializing the same component.
 * This allows the function to be called multiple times safely.
 */
function initializeAllSliders(): void {
  document.querySelectorAll<HTMLElement>('[data-component="slider"]').forEach((container) => {
    if (container.dataset.initialized) {
      return;
    }

    const mask = container.querySelector<HTMLElement>("[data-slider-mask]");
    if (!mask) {
      return;
    }

    const slides = Array.from(mask.querySelectorAll<HTMLElement>("[data-slider-slide]"));
    if (slides.length === 0) {
      return;
    }

    console.log(`Initializing slider with ${slides.length} slides.`, container);

    container.dataset.initialized = "true";

    const prevArrow = container.querySelector<HTMLElement>('[data-slider-arrow="prev"]');
    const nextArrow = container.querySelector<HTMLElement>('[data-slider-arrow="next"]');
    const dotsContainer = container.querySelector<HTMLElement>("[data-slider-dots]");

    initializeSlider({
      container,
      mask,
      slides,
      prevArrow,
      nextArrow,
      dotsContainer,
    });
  });
}

/**
 * Initializes base components that are ready as soon as the DOM loads
 * and do not depend on asynchronous data.
 */
export function initializeBaseComponents(): void {
  console.log("Components: Initializing BASE components (dropdowns, theme)...");
  initThemeToggler();
  initializeAllDropdowns();
  console.log("Components: BASE initialization complete.");
}

/**
 * Initializes dynamic components that might depend on content added
 * after initial page load (e.g., from API calls).
 * This should be called at the end of the main script execution flow.
 */
export function initializeDynamicComponents(): void {
  console.log("Components: Initializing DYNAMIC components (sliders)...");
  initializeAllSliders();
  console.log("Components: DYNAMIC initialization complete.");
}
